import { useCallback, useEffect, useRef, useState } from "react";

export const NOTE_AUDIO_MAX_BYTES = 25 * 1024 * 1024;
const MAX_SECONDS = 30 * 60;

const recordingError = (code) => Object.assign(new Error(code), { code });

export default function useNoteAudioRecorder({ orgId, noteId, onRecorded, onError }) {
  const sessionRef = useRef(null);
  const callbacksRef = useRef({ onRecorded, onError });
  callbacksRef.current = { onRecorded, onError };
  const [starting, setStarting] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);

  const release = useCallback((session) => {
    clearInterval(session.timer);
    clearTimeout(session.deadline);
    if (session.stream && !session.streamStopped) {
      session.streamStopped = true;
      session.stream.getTracks().forEach((track) => track.stop());
    }
    if (sessionRef.current === session) sessionRef.current = null;
  }, []);

  useEffect(() => {
    setStarting(false);
    setRecording(false);
    setSeconds(0);
    return () => {
      const session = sessionRef.current;
      if (!session) return;
      session.cancelled = true;
      if (session.recorder?.state === "recording") session.recorder.stop();
      release(session);
    };
  }, [orgId, noteId, release]);

  const stop = useCallback(() => {
    const session = sessionRef.current;
    if (session?.recorder?.state === "recording") {
      // Capture the duration before the asynchronous final data/stop events.
      session.stoppedAt = Date.now();
      session.recorder.stop();
    }
  }, []);

  const start = useCallback(async () => {
    if (!orgId || !noteId || sessionRef.current) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      callbacksRef.current.onError(recordingError("recording_unavailable"));
      return;
    }
    const session = { cancelled: false, chunks: [] };
    sessionRef.current = session;
    setStarting(true);
    try {
      session.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (session.cancelled) {
        release(session);
        return;
      }
      const mimeType = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((type) =>
        MediaRecorder.isTypeSupported?.(type),
      );
      const recorder = new MediaRecorder(session.stream, {
        ...(mimeType ? { mimeType } : {}),
        audioBitsPerSecond: 64000,
      });
      session.recorder = recorder;
      recorder.ondataavailable = (event) => {
        if (!session.cancelled && event.data?.size) session.chunks.push(event.data);
      };
      recorder.onerror = (event) => {
        if (session.cancelled) return;
        session.cancelled = true;
        if (recorder.state === "recording") recorder.stop();
        release(session);
        setStarting(false);
        setRecording(false);
        setSeconds(0);
        callbacksRef.current.onError(event.error || recordingError("recording_failed"));
      };
      recorder.onstop = () => {
        release(session);
        if (session.cancelled) return;
        setRecording(false);
        setSeconds(0);
        const type = recorder.mimeType || mimeType || session.chunks[0]?.type;
        const blob = new Blob(session.chunks, { type });
        if (!blob.size) {
          callbacksRef.current.onError(recordingError("audio_empty"));
          return;
        }
        const durationSeconds = Math.min(
          MAX_SECONDS,
          Math.max(1, Math.ceil(((session.stoppedAt || Date.now()) - session.startedAt) / 1000)),
        );
        callbacksRef.current.onRecorded({ blob, durationSeconds, orgId, noteId });
      };
      recorder.start(1000);
      session.startedAt = Date.now();
      setRecording(true);
      setSeconds(0);
      session.timer = setInterval(
        () =>
          setSeconds(Math.min(MAX_SECONDS, Math.floor((Date.now() - session.startedAt) / 1000))),
        500,
      );
      session.deadline = setTimeout(stop, MAX_SECONDS * 1000);
    } catch (error) {
      if (!session.cancelled) {
        release(session);
        setRecording(false);
        callbacksRef.current.onError(error);
      }
    } finally {
      if (!session.cancelled) setStarting(false);
    }
  }, [orgId, noteId, release, stop]);

  return { starting, recording, seconds, start, stop };
}
